import { UserType } from 'hooks/api/retryAccountCreation';
import { ID, PaginationContext, PaginationParams } from './api.common.types';

export type UserManagerInfo = {
  email: string;
  userName: string;
  phoneNumber: string;
  custodianType?: string;
  isDefaultForCustodianType?: boolean;
} | null;

export type UserManagers = UserManagerInfo[];

export enum UserProfileTypes {
  roth = 'Roth',
  traditional = 'Traditional',
  inheritedTraditional = 'Inherited Traditional',
  inheritedRoth = 'Inherited Roth',
  hsa = 'HSA',
  esa = 'ESA',
  sepFreelancer = 'SEP IRA (freelancer)',
  simpleIra = 'SIMPLE IRA',
  solo401kFreelancer = 'Solo 401(k)',
  sepIra = 'SEP IRA (employee)',
  rothSolo401k = 'Roth Solo 401(k)',
  rothSepIra = 'Roth SEP IRA',
  rothSimpleIra = 'Roth SIMPLE IRA',
  corporate401kSubAccount = 'Corporate 401k Sub-account',
}

export const FreelancerAccountTypes = [
  UserProfileTypes.sepFreelancer,
  UserProfileTypes.simpleIra,
  UserProfileTypes.solo401kFreelancer,
  UserProfileTypes.rothSolo401k,
  UserProfileTypes.rothSepIra,
  UserProfileTypes.rothSimpleIra,
  UserProfileTypes.corporate401kSubAccount,
] as const;

export enum CustodianTypes {
  solera = 'Solera',
  iraClub = 'IRA Club',
  madisonTrust = 'Madison Trust',
  advantaIra = 'Advanta IRA',
  entrust = 'Entrust',
  forgeTrust = 'Forge Trust',
  iPlanGroup = 'iPlan Group',
  cnbCustody = 'CNB Custody',
  inspira = 'Inspira',
  mountainWestIra = 'Mountain West IRA',
  midAtlanticIra = 'MidAtlantic IRA',
  horizonTrust = 'Horizon Trust',
  nextGenTrust = 'Next Gen Trust',
  camaPlan = 'CamaPlan',
}

export type CustodianType = keyof typeof CustodianTypes;

// Common type of users
export type User = {
  id: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  userName: string;
  phoneNumber?: string;
  email: string;
  profileStatus: string;
  created: string;
  lastLogin?: string;
  isAutoLocked: boolean;
  isLocked: boolean;
  hoursToAutoUnlock: number;
  lockReason?: string;
  userDataIsInconsistent: boolean;
  error?: string[];
  approvalDateTime?: string;
  nextBillingDate?: string;
  custodianType: string;
  userManagers?: UserManagers;
  balance: number;
  employerId?: number;
  hasUserManager: boolean;
  accountClosed: boolean;
};

export type FullName = {
  fullName: string;
};

// IRA users
type IraUserSign = {
  accountNumber?: string;
};
// Once of ira users
export type IraUser = IraUserSign &
  User & {
    profileType: string;
    annualFee?: number;
    etzFee: number;
    custodyFeePercentage?: number | string;
    custodyFeeAbsolute?: number | string;
  };
export type IraUserResponse = IraUser;
// List of ira users
export type IraUsersResponse = {
  items: IraUser[];
  context: PaginationContext;
};
export enum IraUserFieldNames {
  email = 'email',
  firstName = 'firstName',
  middleName = 'middleName',
  lastName = 'lastName',
  custodianType = 'custodianType',
  profileType = 'profileType',
  etzFee = 'etzFee',
  annualFee = 'annualFee',
  nextBillingDate = 'nextBillingDate',
  custodyFeePercentage = 'custodyFeePercentage',
  custodyFeeAbsolute = 'custodyFeeAbsolute',
  employerName = 'employerName',
}

export type IraUserRequest = {
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  etzFee: number;
  nextBillingDate?: string;
  custodyFeePercentage?: string | number;
  custodyFeeAbsolute?: string | number;
  custodianType: string;
  profileType?: string;
};

export type IraUserRequestForm = IraUserRequest;

export type UserEmailCheckResponse = Pick<
  User,
  'firstName' | 'middleName' | 'lastName' | 'email'
> & {
  profileStatus?: string;
  annualFee?: number;
  etzFee?: number;
  custodyFeePercentage?: string | number;
  profileType?: string;
};

export type IraUserCheckPayload = IraUserRequest &
  UserEmailCheckResponse & { employerId?: number };

export type LockUserRequest = {
  comment: string;
};

export type LockUserPayload = LockUserRequest;
export type LockUserType = {
  id: ID;
  payload: LockUserRequest;
};
export type UnlockUserType = {
  id: ID;
  isCancelAutoLock: boolean;
};

export type IndependentIraUser = User & {
  profileType: string;
  accountNumber: string;
  annualFee: number;
  etzFee: number;
  nextBillingDate?: string;
  custodyFeePercentage?: number | string;
  custodyFeeAbsolute?: number | string;
};

export type IndependentIraUserCheckPayload = IndependentIraUserRequest &
  UserEmailCheckResponse;

export type IndependentIraUserRequestForm = Omit<
  IndependentIraUserRequest,
  'annualFee'
> & {
  annualFee: string | number;
};

export type IndependentIraUserRequest = {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  profileType: string;
  custodianType: string;
  annualFee: number;
  etzFee: number;
  nextBillingDate?: string;
  custodyFeePercentage?: number | string;
  custodyFeeAbsolute?: number | string;
  businessName?: string;
};

export type IraAccountsTableUser = Omit<IndependentIraUser, 'profileType'> & {
  userProfileType: string;
};

export type IndependentIraUsersResponse = {
  items: IraAccountsTableUser[];
  context: PaginationContext;
  totalProfileBalance?: number;
  userIraCount?: number;
  totalOrderFeeAmount?: number;
  totalCustodyFeeAmount?: number;
  search?: string;
};

export type IndependentIraUsersMetricsPayload = Omit<
  PaginationParams,
  'page' | 'size' | 'orderBy' | 'orderByDesc'
>;

export type IndependentIraUsersMetricsResponse = {
  totalProfileBalance: number;
  userIraCount: number;
  totalOrderFeeAmount: number;
  totalCustodyFeeAmount: number;
  totalWithdrawalFeeAmount: number;
};

export type RetryAccountCreationType = {
  id: ID;
  userType: UserType;
};

export type ExportUsersPayload = Omit<PaginationParams, 'page' | 'size'>;

export type UserToAssignWalletTransfer = FullName &
  IraUserSign &
  Pick<User, 'firstName' | 'middleName' | 'lastName' | 'id'> &
  Pick<IraAccountsTableUser, 'userProfileType'> & { hasActivePreNotifyDeposit: boolean };

export type UsersToAssignWalletTransferResponse = {
  items: UserToAssignWalletTransfer[];
};
